import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Mic, Activity, Volume2, X, Loader2, Radio } from 'lucide-react';
import { apiFetch } from '../services/apiClient';

interface SttStats { activeSessions: number; totalSessions: number; totalMs: number; totalBytes: number; estimatedCostCny: string; recentEnded: { uid: string; durationS: number; bytes: number; timeAgo: string }[]; }
interface TtsStats { totalCalls: number; totalChars: number; estimatedCostCny: string; recentCalls: { uid: string; chars: number; voiceId: string; timeAgo: string }[]; }
interface VoiceUsage { stt: SttStats; tts: TtsStats; summary: { totalCostCny: string }; }

function formatDuration(ms: number): string {
  if (ms < 1000) return ms + 'ms';
  const s = Math.round(ms / 1000);
  if (s < 60) return s + 's';
  if (s < 3600) return Math.floor(s / 60) + 'm ' + (s % 60) + 's';
  return Math.floor(s / 3600) + 'h ' + Math.floor((s % 3600) / 60) + 'm';
}

function formatBytes(b: number): string {
  if (b < 1024) return b + 'B';
  if (b < 1048576) return (b / 1024).toFixed(1) + 'KB';
  return (b / 1048576).toFixed(1) + 'MB';
}

export function VoiceUsagePanel({
  isOpen, onClose, buttonRect
}: {
  isOpen: boolean; onClose: () => void; buttonRect: { top: number; right: number } | null;
}) {
  const [data, setData] = useState<VoiceUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const fetchData = async () => {
      try {
        const res = await apiFetch('/api/voice/usage');
        const json = await res.json();
        if (json && json.stt) setData(json as VoiceUsage);
      } catch {} finally { setLoading(false); }
    };
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [isOpen]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const stt = data?.stt;
  const tts = data?.tts;
  const sttCost = stt ? parseFloat(stt.estimatedCostCny.replace('¥', '')) : 0;
  const ttsCost = tts ? parseFloat(tts.estimatedCostCny.replace('¥', '')) : 0;
  const totalCost = sttCost + ttsCost;

  // Precision monitor: log warning if frontend sum differs from backend total by more than 0.0001
  // v3.13.4: 此 useEffect 必须位于所有早返回之前，否则违反 Hooks 规则（渲染间 hooks 数量不一致 → Rendered more hooks）
  useEffect(() => {
    if (!data?.summary?.totalCostCny) return;
    const backendTotal = parseFloat(data.summary.totalCostCny.replace('¥', ''));
    const frontendTotal = sttCost + ttsCost;
    const precisionDiff = Math.abs(backendTotal - frontendTotal);
    if (precisionDiff > 0.0001) {
      console.warn(
        `[VoiceUsage] Cost precision mismatch: backend=${backendTotal}, frontend=${frontendTotal}, diff=${precisionDiff.toFixed(6)}`
      );
    }
  }, [data, sttCost, ttsCost]);

  if (!isOpen || !buttonRect) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        ref={panelRef}
        initial={{ opacity: 0, x: -8, scale: 0.96 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: -8, scale: 0.96 }}
        transition={{ duration: 0.15 }}
        style={{ position: 'fixed', bottom: window.innerHeight - buttonRect.top + 8, left: buttonRect.right + 8, zIndex: 100 }}
        className="w-72 max-h-[70vh] overflow-y-auto custom-scrollbar rounded-xl border border-white/10 bg-zinc-900/95 p-4 shadow-2xl backdrop-blur-xl"
      >
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-black uppercase tracking-widest text-white/60">语音用量</h4>
          <button type="button" onClick={onClose} className="rounded-md border border-white/20 bg-white/5 p-1 text-white/70 hover:bg-red-500/20 hover:border-red-500/40 hover:text-red-400 transition-colors"><X size={16} /></button>
        </div>

        {loading && !data ? (
          <div className="py-6 text-center text-white/30"><Loader2 size={20} className="animate-spin mx-auto" /></div>
        ) : (
          <>
            {/* 总计 */}
            <div className="mb-3 flex items-baseline gap-3">
              <span className="text-3xl font-black font-mono text-celestial-saturn">
                {'¥' + totalCost.toFixed(4)}
              </span>
              <div className="flex flex-col text-[10px] text-white/40">
                <span>今日总费用</span>
                <span className="font-mono text-white/55 font-bold">STT + TTS</span>
              </div>
            </div>

            {/* STT 块 */}
            <div className="mb-3 rounded-lg border border-white/8 bg-white/[0.03] p-3">
              <div className="flex items-center gap-2 mb-2">
                <Mic size={14} className="text-celestial-saturn" />
                <span className="text-xs font-bold text-white/60">语音识别 (STT)</span>
                {stt && stt.activeSessions > 0 && (
                  <span className="flex items-center gap-1 text-[10px] text-emerald-400 ml-auto">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    {stt.activeSessions} 活跃
                  </span>
                )}
              </div>
              {stt ? (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-white/45">今日会话</span>
                    <span className="font-mono text-white/70">{stt.totalSessions}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-white/45">音频时长</span>
                    <span className="font-mono text-white/70">{formatDuration(stt.totalMs)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-white/45">数据量</span>
                    <span className="font-mono text-white/70">{formatBytes(stt.totalBytes)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-white/45">预估费用</span>
                    <span className="font-mono text-amber-400">{stt.estimatedCostCny}</span>
                  </div>
                </div>
              ) : <p className="text-xs text-white/25">暂无 STT 数据</p>}
            </div>

            {/* TTS 块 */}
            <div className="mb-3 rounded-lg border border-white/8 bg-white/[0.03] p-3">
              <div className="flex items-center gap-2 mb-2">
                <Volume2 size={14} className="text-violet-400" />
                <span className="text-xs font-bold text-white/60">语音合成 (TTS)</span>
              </div>
              {tts ? (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-white/45">今日合成次数</span>
                    <span className="font-mono text-white/70">{tts.totalCalls}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-white/45">合成字符数</span>
                    <span className="font-mono text-white/70">{tts.totalChars.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-white/45">预估费用</span>
                    <span className="font-mono text-violet-400">{tts.estimatedCostCny}</span>
                  </div>
                </div>
              ) : <p className="text-xs text-white/25">暂无 TTS 数据</p>}
            </div>

            {/* 最近记录 */}
            {((stt?.recentEnded?.length ?? 0) > 0 || (tts?.recentCalls?.length ?? 0) > 0) && (
              <div className="mt-2 pt-2 border-t border-white/5 text-[10px] text-white/30">
                最近活动 · STT {stt?.recentEnded?.length || 0} 次 · TTS {tts?.recentCalls?.length || 0} 次
              </div>
            )}
          </>
        )}
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
